// Dashboard-level filter bar — Channel / Store / Date range.
//
// Locked mechanism (ai-chat-dashboard-tracker.md "Dashboard-level filters"):
// filter VALUES picked here are sent to the backend's /refresh endpoint,
// which binds them as named Redshift Data API parameters into each
// widget's saved SQL — never spliced into a SQL string client- or
// server-side. This component only collects a selection; it has no idea
// how the values get used downstream.
//
// Channel: small known set, real dropdown fetched once via
// GET /api/dashboards/filter-options/channels (distinct-values query,
// not hardcoded).
// Store: hundreds of thousands of possible values (fact.stores) — a type-
// and-search Autosuggest querying GET .../filter-options/stores?q=...,
// same UX shape as the dashboard-sharing username search.
// Date: standard Cloudscape DateRangePicker, absolute-only (no relative
// presets — a saved dashboard's "last used" filter selection should be a
// stable date range, not a relative one that silently drifts every time
// the dashboard is reopened).

import React, { useState, useEffect, useRef } from 'react';
import { Select, Autosuggest, DateRangePicker, Button } from '@cloudscape-design/components';
import { dashboardsApi } from './dashboardsApi.js';

function toDateRangePickerValue(startDate, endDate) {
  if (!startDate || !endDate) return null;
  return { type: 'absolute', startDate, endDate };
}

export default function DashboardFilterBar({ filters, onChange, onApply, applying }) {
  const [channelOptions, setChannelOptions] = useState([]);
  const [storeQuery, setStoreQuery] = useState(filters.shop_id ? String(filters.shop_id) : '');
  const [storeOptions, setStoreOptions] = useState([]);
  const storeSearchTimer = useRef(null);

  useEffect(() => {
    dashboardsApi.getChannelOptions()
      .then((data) => setChannelOptions((data.channels || []).map((c) => ({ label: c, value: c }))))
      .catch(() => setChannelOptions([]));
  }, []);

  const handleStoreInputChange = (value) => {
    setStoreQuery(value);
    if (storeSearchTimer.current) clearTimeout(storeSearchTimer.current);
    storeSearchTimer.current = setTimeout(() => {
      if (!value || value.length < 2) {
        setStoreOptions([]);
        return;
      }
      dashboardsApi.searchStoreOptions(value)
        .then((data) => setStoreOptions((data.stores || []).map((s) => ({
          label: `${s.shop_name} (#${s.shop_id})`,
          value: String(s.shop_id),
        }))))
        .catch(() => setStoreOptions([]));
    }, 300);
  };

  const dateValue = toDateRangePickerValue(filters.start_date, filters.end_date);

  // Rendered inline inside DashboardPage.jsx's dark scope bar (6 Aug 2026
  // masthead redesign) — no own background/label styling here, each field
  // gets a light "pill" wrapper (.ai-chat-dashboard-scope-field) so
  // Cloudscape's controls read cleanly against the dark strip instead of
  // needing their internals restyled directly.
  return (
    <>
      <div className="ai-chat-dashboard-scope-field">
        <label className="ai-chat-dashboard-scope-field-label">Channel</label>
        <Select
          selectedOption={filters.channel ? { label: filters.channel, value: filters.channel } : { label: 'All', value: '' }}
          onChange={({ detail }) => onChange({ ...filters, channel: detail.selectedOption.value || null })}
          options={[{ label: 'All', value: '' }, ...channelOptions]}
          placeholder="All channels"
        />
      </div>

      <div className="ai-chat-dashboard-scope-field">
        <label className="ai-chat-dashboard-scope-field-label">Store</label>
        <Autosuggest
          value={storeQuery}
          onChange={({ detail }) => handleStoreInputChange(detail.value)}
          onSelect={({ detail }) => {
            onChange({ ...filters, shop_id: detail.selectedOption?.value || null });
          }}
          options={storeOptions}
          placeholder="All stores — type to search"
          empty="No matching stores"
          enteredTextLabel={(v) => v}
        />
      </div>

      <div className="ai-chat-dashboard-scope-field">
        <label className="ai-chat-dashboard-scope-field-label">Date range</label>
        <DateRangePicker
          value={dateValue}
          onChange={({ detail }) => {
            if (!detail.value) {
              onChange({ ...filters, start_date: null, end_date: null });
              return;
            }
            onChange({ ...filters, start_date: detail.value.startDate, end_date: detail.value.endDate });
          }}
          relativeOptions={[]}
          rangeSelectorMode="absolute-only"
          dateOnly
          isValidRange={(range) => {
            if (!range || range.type !== 'absolute') return { valid: true };
            if (!range.startDate || !range.endDate) {
              return { valid: false, errorMessage: 'Select both a start and end date.' };
            }
            return { valid: true };
          }}
          placeholder="All time"
          i18nStrings={{
            todayAriaLabel: 'Today',
            clearButtonLabel: 'Clear',
            cancelButtonLabel: 'Cancel',
            applyButtonLabel: 'Apply',
          }}
        />
      </div>

      <div className="ai-chat-dashboard-scope-apply">
        <Button variant="primary" onClick={onApply} loading={applying}>
          Apply filters
        </Button>
      </div>
    </>
  );
}
